import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useCreateInspection, useUpdateInspection } from '../../../api/inspections';
import { useEnumLabel } from '../../../api/enums';
import { useNavigateWithToast } from '../../../components/common/navigateWithToast';
import { isApiError } from '../../../api/errors';
import { Button, ButtonGroup, SummaryList, type ErrorSummaryItem } from '../../../components/idsk';
import { partsToIso } from '../../../components/idsk/DateInput';
import { formatDate } from '../../../lib/format';
import { useWizard } from './WizardContext';
import { WizardStep, useStepPath } from './WizardStep';

/** Which wizard step owns a server-side field error. */
const FIELD_STEP: Record<string, number> = { name: 1, subjectId: 1, type: 1, task: 1, members: 2, leader: 2, dateFrom: 3, dateTo: 3 };

export function Step4Summary() {
  const { t } = useTranslation();
  const { state, reset, editing } = useWizard();
  const label = useEnumLabel();
  const stepPath = useStepPath();
  const create = useCreateInspection();
  const update = useUpdateInspection(editing?.id ?? '');
  const navigateWithToast = useNavigateWithToast();
  const navigate = useNavigate();
  const [errors, setErrors] = useState<ErrorSummaryItem[]>([]);
  const [submitCount, setSubmitCount] = useState(0);
  const from = partsToIso(state.dateFrom);
  const to = partsToIso(state.dateTo);
  const leader = state.members.find((m) => m.id === state.leaderId);

  const submit = async () => {
    setErrors([]);
    try {
      const request = {
        name: state.name.trim(),
        subjectId: state.subject?.id ?? '',
        type: state.type,
        task: state.task || null,
        dateFrom: from.kind === 'valid' ? from.iso : '',
        dateTo: to.kind === 'valid' ? to.iso : '',
        members: state.members.map((m) => ({ inspectorId: m.id, leader: m.id === state.leaderId })),
      };
      if (editing) {
        await update.mutateAsync({ ...request, version: editing.version });
        reset();
        navigateWithToast(`/inspekcie/${editing.id}`, t('wizard.edited', { number: editing.number }));
        return;
      }
      const created = await create.mutateAsync(request);
      // The wizard state is cleared by the confirmation page (clearing it here would re-trigger the step guard).
      void navigate(`/inspekcie/nova/hotovo`, { state: { id: created.id, number: created.number, name: created.name } });
    } catch (e) {
      setSubmitCount((c) => c + 1);
      if (isApiError(e) && e.isVersionConflict) {
        setErrors([{ text: t('errors.versionConflict') }]);
        return;
      }
      if (isApiError(e) && e.fieldErrors.length > 0) {
        setErrors(e.fieldErrors.map((fe) => ({ text: fe.message, to: FIELD_STEP[fe.field] ? stepPath(FIELD_STEP[fe.field]!, true) : undefined })));
      } else {
        setErrors([{ text: isApiError(e) ? e.message : t('errors.unexpected') }]);
      }
    }
  };

  const change = (step: number, what: string) => [{ to: stepPath(step, true), text: t('common.change'), visuallyHiddenText: what }];

  return (
    <WizardStep step={4} title={t('wizard.step4.title')} errors={errors} submitCount={submitCount}>
      <SummaryList
        rows={[
          { key: t('wizard.step1.name'), value: state.name, actions: change(1, t('wizard.step4.changeName')) },
          { key: t('wizard.step1.subject'), value: state.subject?.label, actions: change(1, t('wizard.step4.changeSubject')) },
          { key: t('wizard.step1.type'), value: label('inspectionTypes', state.type), actions: change(1, t('wizard.step4.changeType')) },
          ...(state.task ? [{ key: t('wizard.step1.task'), value: label('inspectionTasks', state.task), actions: change(1, t('wizard.step4.changeTask')) }] : []),
          {
            key: t('wizard.step4.team'),
            value: (
              <ul className="govuk-list">
                {state.members.map((m) => (
                  <li key={m.id}>{m.displayName}</li>
                ))}
              </ul>
            ),
            actions: change(2, t('wizard.step4.changeTeam')),
          },
          { key: t('wizard.step2.leader'), value: leader?.displayName, actions: change(2, t('wizard.step4.changeLeader')) },
          {
            key: t('wizard.step3.title'),
            value: from.kind === 'valid' && to.kind === 'valid' ? t('wizard.step4.range', { from: formatDate(from.iso), to: formatDate(to.iso) }) : '',
            actions: change(3, t('wizard.step4.changeTimeframe')),
          },
        ]}
      />
      {!editing && <p className="govuk-body">{t('wizard.step4.statusNote')}</p>}
      <ButtonGroup>
        <Button onClick={() => void submit()} preventDoubleClick>
          {editing ? t('wizard.step4.saveChanges') : t('wizard.step4.create')}
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            reset();
            void navigate(editing ? `/inspekcie/${editing.id}` : '/inspekcie');
          }}
        >
          {t('wizard.cancel')}
        </Button>
      </ButtonGroup>
    </WizardStep>
  );
}
